import { presentationFrames } from '../actions/actions';
import type { BoardDoc } from '../collab/BoardDoc';
import { renderRectToCanvas } from './exportBoard';

interface SlideImage {
  dataUrl: string;
  w: number;
  h: number;
  name: string;
}

function filenameFor(doc: BoardDoc): string {
  return doc.getMeta().name.replace(/[^\w\-. ]+/g, '_').trim() || 'board';
}

async function renderSlides(doc: BoardDoc, scale = 2): Promise<SlideImage[]> {
  const frames = presentationFrames(doc);
  const slides: SlideImage[] = [];
  for (const frame of frames) {
    const canvas = await renderRectToCanvas(
      doc,
      { x: frame.x, y: frame.y, w: frame.w, h: frame.h },
      { scale },
    );
    slides.push({ dataUrl: canvas.toDataURL('image/png'), w: frame.w, h: frame.h, name: frame.name || 'Слайд' });
  }
  return slides;
}

/**
 * One PDF page per frame, in presentation order, each page sized to match
 * its frame — so a portrait sticky-note recap and a widescreen deck slide can
 * live in the same export without either being stretched.
 */
export async function exportFramesToPdf(doc: BoardDoc): Promise<boolean> {
  const slides = await renderSlides(doc, 2);
  if (slides.length === 0) return false;

  const { jsPDF } = await import('jspdf');
  const first = slides[0]!;
  const pdf = new jsPDF({
    orientation: first.w >= first.h ? 'landscape' : 'portrait',
    unit: 'pt',
    format: [first.w, first.h],
  });

  slides.forEach((slide, i) => {
    if (i > 0) pdf.addPage([slide.w, slide.h], slide.w >= slide.h ? 'landscape' : 'portrait');
    pdf.addImage(slide.dataUrl, 'PNG', 0, 0, slide.w, slide.h);
  });

  pdf.save(`${filenameFor(doc)}.pdf`);
  return true;
}

/**
 * One slide per frame. PowerPoint decks have a single canvas size for every
 * slide, unlike PDF pages, so frames that are not all the same aspect ratio
 * are letterboxed onto the first frame's shape rather than stretched.
 */
export async function exportFramesToPptx(doc: BoardDoc): Promise<boolean> {
  const slides = await renderSlides(doc, 2);
  if (slides.length === 0) return false;

  const PptxGenJS = (await import('pptxgenjs')).default;
  const pptx = new PptxGenJS();

  const base = slides[0]!;
  const deckW = 10; // inches — a conventional widescreen-ish deck width
  const deckH = Math.max(deckW * (base.h / base.w), 1);
  pptx.defineLayout({ name: 'BOARD', width: deckW, height: deckH });
  pptx.layout = 'BOARD';

  for (const slide of slides) {
    const s = pptx.addSlide();
    const fit = Math.min(deckW / slide.w, deckH / slide.h);
    const w = slide.w * fit;
    const h = slide.h * fit;
    s.background = { color: 'FFFFFF' };
    s.addImage({ data: slide.dataUrl, x: (deckW - w) / 2, y: (deckH - h) / 2, w, h });
  }

  await pptx.writeFile({ fileName: `${filenameFor(doc)}.pptx` });
  return true;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

/** Opens a print-ready tab, one frame per page, and triggers the print dialog. */
export async function printFrames(doc: BoardDoc): Promise<boolean> {
  const slides = await renderSlides(doc, 2);
  if (slides.length === 0) return false;

  const win = window.open('', '_blank');
  if (!win) return false;

  const pages = slides
    .map((s) => `<div class="page"><img src="${s.dataUrl}" alt="${escapeHtml(s.name)}" /></div>`)
    .join('');

  win.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(doc.getMeta().name)}</title>
    <style>
      @page { margin: 0; }
      * { box-sizing: border-box; }
      body { margin: 0; background: #fff; }
      .page {
        width: 100vw;
        height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        page-break-after: always;
        overflow: hidden;
      }
      .page:last-child { page-break-after: auto; }
      .page img { max-width: 100%; max-height: 100%; }
    </style>
  </head>
  <body>${pages}</body>
</html>`);
  win.document.close();

  // Data-URL images in a freshly written document are already decoded
  // synchronously in every browser this app targets, but a short delay is
  // cheap insurance against the print dialog opening over a blank page.
  setTimeout(() => {
    win.focus();
    win.print();
  }, 250);

  return true;
}
